'use client';

/**
 * SmartLink Component
 *
 * Universal link component that handles all link types:
 * - External links (https://) → <a target="_blank" rel="noopener noreferrer">
 * - Internal links (/path) → Next.js <Link>
 * - Email links (mailto:) → <a>
 * - Phone links (tel:) → <a> with sanitized number
 * - Downloads → <DownloadLink> with toast feedback
 * - Custom patterns → Project-specific resolvers from @custom/link-resolvers
 *
 * This is the single source of truth for link rendering logic.
 */

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { DownloadLink } from '@/app/_components/core/download-button';
import { useLabel } from '@/app/_contexts/labels-context';
import { getDownloadUrl, getFilenameFromUrl } from '@/app/_lib/download-url';
import { emptyToUndefined } from '@/app/_lib/utils';
import { customLinkResolvers } from '@custom/link-resolvers';
import { getLinkType } from './smart-link.utils';
import type { SmartLinkProps } from './smart-link.types';

/**
 * Checks if a URL is an external link
 */
function isExternalLink(url: string): boolean {
  return getLinkType(url) === 'external';
}

/**
 * Builds the href for internal links - adds leading slash if missing
 * Anchor links (#...) are returned as-is for same-page navigation
 */
function buildHref(slug: string): string {
  if (isExternalLink(slug)) return slug;
  if (slug.startsWith('/')) return slug;
  if (slug.startsWith('#')) return slug;
  return `/${slug}`;
}

/**
 * Creates a click handler for anchor links that ensures scrolling
 * even when clicking the same anchor twice (browser normally ignores this).
 *
 * Uses scrollIntoView so the header offset comes from CSS `scroll-margin-top`
 * (declared globally on `[id]`), keeping that calculation in exactly one place.
 * The follow-up layout corrections (e.g. Curator reflows pushing the target
 * away) are handled by the `useAnchorScroll` hook listening to `pushState`.
 */
function createAnchorClickHandler(
  href: string,
  existingOnClick?: React.MouseEventHandler<HTMLAnchorElement>
): React.MouseEventHandler<HTMLAnchorElement> | undefined {
  if (!href.startsWith('#')) return existingOnClick;

  return (e) => {
    e.preventDefault();
    existingOnClick?.(e);

    const targetId = href.slice(1);
    if (!targetId) return;

    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      // Element is in the DOM — smooth-scroll. CSS scroll-margin-top adds
      // the header offset; pushState updates the URL and (via our patched
      // history API) wakes the useAnchorScroll watcher for late reflows.
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', href);
    } else {
      // Element not in DOM (e.g. inside a closed accordion) —
      // update the hash and dispatch hashchange so hash-aware components
      // (like accordion-card-list) can open themselves and scroll.
      const oldURL = window.location.href;
      window.history.pushState(null, '', href);
      window.dispatchEvent(
        new HashChangeEvent('hashchange', { oldURL, newURL: window.location.href })
      );
    }
  };
}

/**
 * Sanitizes tel: links by removing spaces, parentheses, dashes,
 * and optional zero notation like "(0)".
 */
function sanitizeTelLink(url: string): string {
  if (!url.startsWith('tel:')) return url;
  return url
    .replace(/\(0\)/g, '') // Remove optional zero notation
    .replace(/[\s()\-]/g, ''); // Remove spaces, parentheses, dashes
}

/**
 * Extracts the hash fragment from same-page links.
 * For "/current-page#section" when on "/current-page", returns "#section".
 * For pure hash links or cross-page links, returns the href unchanged.
 */
function extractSamePageHash(href: string, pathname: string): string {
  const hashIndex = href.indexOf('#');
  if (hashIndex <= 0) return href; // pure hash or no hash
  if (href.substring(0, hashIndex) === pathname) return href.substring(hashIndex);
  return href;
}

export function SmartLink({
  href,
  title,
  className,
  target,
  rel,
  children,
  download,
  onClick,
  'aria-label': ariaLabel,
  'aria-current': ariaCurrent,
  hrefLang,
  'data-track-id': dataTrackId,
}: SmartLinkProps) {
  const content = children ?? title;
  const pathname = usePathname();
  const opensInNewTabLabel = useLabel('a11y.opensInNewTab', 'opens in new tab');

  // Passive tracking attribute — no event is fired here. Only stamped when a
  // caller assigned a stable key; the locale is NOT duplicated (it lives on
  // `<html lang>`). Spread into every rendered anchor below.
  const trackingAttrs: { 'data-track-id'?: string } = dataTrackId
    ? { 'data-track-id': dataTrackId }
    : {};

  // 1. Check if any custom resolver handles this link pattern
  const resolver = customLinkResolvers.find((r) => r.pattern.test(href));
  if (resolver) {
    return <>{resolver.render({ href, title, className, target, children: content })}</>;
  }

  // 2. Download links → use DownloadLink with toast feedback
  if (download) {
    const filename = typeof download === 'string'
      ? download
      : getFilenameFromUrl(href);
    const proxyUrl = getDownloadUrl(href, filename);

    return (
      <DownloadLink
        href={proxyUrl}
        filename={filename}
        className={className}
        {...trackingAttrs}
      >
        {content}
      </DownloadLink>
    );
  }

  // 3. Handle by link type
  const linkType = getLinkType(href);

  switch (linkType) {
    case 'mailto':
      return (
        <a href={href} className={className} target={target} rel={rel} onClick={onClick} aria-label={ariaLabel} aria-current={ariaCurrent} hrefLang={hrefLang} {...trackingAttrs}>
          {content}
        </a>
      );

    case 'tel':
      return (
        <a
          href={sanitizeTelLink(href)}
          className={className}
          target={target}
          rel={rel}
          onClick={onClick}
          aria-label={ariaLabel}
          aria-current={ariaCurrent}
          hrefLang={hrefLang}
          {...trackingAttrs}
        >
          {content}
        </a>
      );

    case 'external': {
      // Editor-driven: only force a new tab when TYPO3 explicitly sets it.
      // Empty string or undefined → render without a target.
      const effectiveTarget = emptyToUndefined(target);
      const opensInNewTab = effectiveTarget === '_blank';
      // rel="noopener noreferrer" only matters when opening a new tab
      // (tabnabbing protection + referrer policy). Without _blank it's
      // semantic noise, so we only set it then — or honor an explicit rel.
      const effectiveRel = rel ?? (opensInNewTab ? 'noopener noreferrer' : undefined);
      // a11y: announce "opens in new tab" only when it actually does
      const linkText = typeof content === 'string' ? content : title;
      const externalAriaLabel =
        ariaLabel ??
        (opensInNewTab && linkText
          ? `${linkText} (${opensInNewTabLabel})`
          : undefined);
      return (
        <a
          href={href}
          className={className}
          target={effectiveTarget}
          rel={effectiveRel}
          onClick={onClick}
          aria-label={externalAriaLabel}
          aria-current={ariaCurrent}
          hrefLang={hrefLang}
          {...trackingAttrs}
        >
          {content}
        </a>
      );
    }

    case 'internal':
    default: {
      const builtHref = buildHref(href);
      // For same-page links with hash (e.g. "/current-page#section"),
      // treat as anchor navigation so the custom scroll/hashchange handler kicks in.
      const anchorHref = extractSamePageHash(builtHref, pathname);

      return (
        <Link
          href={builtHref}
          className={className}
          target={target}
          rel={rel}
          onClick={createAnchorClickHandler(anchorHref, onClick)}
          aria-label={ariaLabel}
          aria-current={ariaCurrent}
          hrefLang={hrefLang}
          {...trackingAttrs}
        >
          {content}
        </Link>
      );
    }
  }
}
